import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is cookie-based (HttpOnly, Secure, SameSite=Lax). No token is
// persisted in localStorage anymore — the June 2026 audit flagged
// localStorage tokens as an XSS surface. We rely entirely on cookies.
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Refresh token rotation: single-flight guard.
let refreshPromise = null;
let onAuthFailure = null;
export function setOnAuthFailure(cb) { onAuthFailure = cb; }

// Session hint (item QA-P3-03): a plain, non-secret localStorage flag that
// records whether this browser has ever established an authenticated session.
// It is NOT an auth token and grants no access — it only lets the client skip
// the noisy `/auth/refresh` retry for first-time public visitors who have no
// cookie at all. Real sessions still refresh normally. The single `/auth/me`
// probe still fires (authoritative session detection is never weakened).
const SESSION_HINT_KEY = "ntn_session_hint";
export function markSession() { try { localStorage.setItem(SESSION_HINT_KEY, "1"); } catch (_e) { /* noop */ } }
export function clearSession() { try { localStorage.removeItem(SESSION_HINT_KEY); } catch (_e) { /* noop */ } }
function hadSession() { try { return localStorage.getItem(SESSION_HINT_KEY) === "1"; } catch (_e) { return false; } }

const NO_REFRESH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthEndpoint = NO_REFRESH_PATHS.some((p) => url.includes(p));

    // Skip the refresh retry entirely when this browser has never had a
    // session — a public first-time visitor has no cookie to refresh, so the
    // retry would only produce an expected-but-noisy 401 in the console.
    if (status !== 401 || original?._retried || isAuthEndpoint || !hadSession()) {
      return Promise.reject(error);
    }
    // v1.0.2 session policy: background/polling traffic must not keep a
    // session alive. Only auto-refresh when the user interacted recently.
    try {
      const lastAct = Number(localStorage.getItem("ntn_last_activity") || 0);
      if (Date.now() - lastAct > 30 * 60 * 1000) {
        return Promise.reject(error);
      }
    } catch (_e) { /* noop */ }
    original._retried = true;

    try {
      if (!refreshPromise) {
        refreshPromise = apiClient.post("/auth/refresh")
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      return apiClient(original);
    } catch (e) {
      // Clear any legacy token that may still be present from an older
      // client version (June 2026 audit) — safe no-op if empty.
      try { localStorage.removeItem("ntn_token"); } catch (_e) { /* noop */ }
      clearSession();
      if (onAuthFailure) onAuthFailure();
      return Promise.reject(error);
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const REPORT_TYPES = [
  { key: "credit_report", label: "DecisionPoint Report", desc: "FICO score, debt history, accounts.", group: "intelligence" },
  { key: "criminal_report", label: "Criminal Report", desc: "Federal, state, county criminal records.", group: "intelligence" },
  { key: "ssn_verification", label: "SSN Verification", desc: "SSN trace and identity match.", group: "intelligence" },
  { key: "employment_verification", label: "Employment Verification", desc: "Confirm employer, role, income.", group: "verification" },
  { key: "landlord_verification", label: "Landlord Verification", desc: "Contact prior landlords; confirm tenancy history.", group: "verification" },
  { key: "document_validation", label: "Document Validation", desc: "Verify ID, paystubs, lease docs.", group: "verification" },
  // Legacy — kept for backward compatibility with screenings ordered before Feb 2026.
  // Hidden from the new order page but still rendered on legacy detail views.
  { key: "eviction_search", label: "Eviction Search", desc: "Nationwide eviction & housing court records.", group: "legacy" },
];

// The recommended full-scope bundle — a UI convenience today; the backend
// persists a `package: "decision_ready"` marker for reporting.
export const DECISION_READY_PACKAGE = [
  "credit_report", "criminal_report", "ssn_verification",
  "employment_verification", "landlord_verification", "document_validation",
];

export const INTELLIGENCE_SERVICES = REPORT_TYPES.filter((r) => r.group === "intelligence");
export const VERIFICATION_SERVICES = REPORT_TYPES.filter((r) => r.group === "verification");
export const ORDERABLE_SERVICES = [...INTELLIGENCE_SERVICES, ...VERIFICATION_SERVICES];

export function reportLabel(key) {
  return REPORT_TYPES.find((r) => r.key === key)?.label || key;
}
